import { useState, useEffect } from 'react';
import { apiFetch } from '../lib/api.js';

interface MetricsData {
  leadsPerWeek: { week: string; count: number }[];
  orgsByStatus: Record<string, number>[];
  orgsBySub: Record<string, number>[];
  newOrgsPerWeek: { week: string; count: number }[];
  silentCount: number;
}

interface SilentAccount {
  id: string;
  name: string;
  status: string;
  subscription_status: string;
  created_at: string;
  last_lead_at: string | null;
  admin_email: string | null;
}

type MetricsTab = 'overview' | 'silent';

export function AdminMetricsPage() {
  const [tab, setTab] = useState<MetricsTab>('overview');
  const [metrics, setMetrics] = useState<MetricsData | null>(null);
  const [silent, setSilent] = useState<SilentAccount[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const m = await apiFetch<MetricsData>('/admin/metrics');
        setMetrics(m);
      } catch { /* metrics failed */ }
      try {
        const s = await apiFetch<{ accounts: SilentAccount[] }>('/admin/silent-accounts');
        setSilent(s.accounts);
      } catch { /* silent accounts failed */ }
      finally { setIsLoading(false); }
    })();
  }, []);

  if (isLoading) return <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="h-32 bg-gray-100 rounded-xl animate-pulse" />)}</div>;

  return (
    <div className="space-y-5">
      <h2 className="text-lg font-semibold text-gray-900">Metrics</h2>

      <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
        {([{ key: 'overview' as const, label: 'Overview' }, { key: 'silent' as const, label: `Silent Accounts (${metrics?.silentCount ?? 0})` }]).map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`flex-1 py-2 text-xs font-medium rounded-md transition-colors ${
              tab === t.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}>{t.label}</button>
        ))}
      </div>

      {tab === 'overview' && metrics && <OverviewTab metrics={metrics} />}
      {tab === 'silent' && <SilentTab accounts={silent} />}
    </div>
  );
}

function OverviewTab({ metrics }: { metrics: MetricsData }) {
  const totalLeadsThisWeek = metrics.leadsPerWeek[metrics.leadsPerWeek.length - 1]?.count ?? 0;
  const totalLeadsLastWeek = metrics.leadsPerWeek[metrics.leadsPerWeek.length - 2]?.count ?? 0;
  const leadsDelta = totalLeadsThisWeek - totalLeadsLastWeek;

  const statusMap = Object.fromEntries((metrics.orgsByStatus as unknown as { status: string; count: number }[]).map(r => [r.status, r.count]));
  const subMap = Object.fromEntries((metrics.orgsBySub as unknown as { subscription_status: string; count: number }[]).map(r => [r.subscription_status, r.count]));
  const totalOrgs = Object.values(statusMap).reduce((a, b) => a + b, 0);

  return (
    <div className="space-y-4">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <KpiCard label="Total Accounts" value={totalOrgs} />
        <KpiCard label="Leads This Week" value={totalLeadsThisWeek} delta={leadsDelta} />
        <KpiCard label="Trial Accounts" value={subMap['trial'] ?? 0} />
        <KpiCard label="Silent (30d)" value={metrics.silentCount} warn={metrics.silentCount > 10} />
      </div>

      {/* Orgs by Status */}
      <div className="bg-white rounded-xl border border-gray-100 p-4">
        <p className="font-medium text-gray-900 text-sm mb-3">Accounts by Status</p>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {Object.entries(statusMap).map(([status, count]) => (
            <div key={status} className="text-center">
              <p className="text-xl font-bold text-gray-900">{count}</p>
              <p className="text-xs text-gray-500">{status.replace(/_/g, ' ')}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Leads per Week */}
      <div className="bg-white rounded-xl border border-gray-100 p-4">
        <p className="font-medium text-gray-900 text-sm mb-3">Leads per Week (last 8 weeks)</p>
        <div className="flex items-end gap-1 h-24">
          {metrics.leadsPerWeek.map((w, i) => {
            const max = Math.max(...metrics.leadsPerWeek.map(x => x.count), 1);
            const pct = (w.count / max) * 100;
            return (
              <div key={i} className="flex-1 flex flex-col items-center gap-1">
                <span className="text-xs text-gray-500">{w.count}</span>
                <div className="w-full bg-primary-500 rounded-t" style={{ height: `${Math.max(pct, 4)}%` }} />
                <span className="text-xs text-gray-400">{new Date(w.week).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* New Orgs per Week */}
      <div className="bg-white rounded-xl border border-gray-100 p-4">
        <p className="font-medium text-gray-900 text-sm mb-3">New Accounts per Week</p>
        <div className="flex items-end gap-1 h-24">
          {metrics.newOrgsPerWeek.map((w, i) => {
            const max = Math.max(...metrics.newOrgsPerWeek.map(x => x.count), 1);
            const pct = (w.count / max) * 100;
            return (
              <div key={i} className="flex-1 flex flex-col items-center gap-1">
                <span className="text-xs text-gray-500">{w.count}</span>
                <div className="w-full bg-green-500 rounded-t" style={{ height: `${Math.max(pct, 4)}%` }} />
                <span className="text-xs text-gray-400">{new Date(w.week).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function KpiCard({ label, value, delta, warn }: { label: string; value: number; delta?: number; warn?: boolean }) {
  return (
    <div className={`bg-white rounded-xl border p-4 ${warn ? 'border-red-200' : 'border-gray-100'}`}>
      <p className={`text-2xl font-bold ${warn ? 'text-red-600' : 'text-gray-900'}`}>{value}</p>
      <p className="text-xs text-gray-500 mt-1">{label}</p>
      {delta !== undefined && delta !== 0 && (
        <p className={`text-xs mt-0.5 font-medium ${delta > 0 ? 'text-green-600' : 'text-red-600'}`}>
          {delta > 0 ? '+' : ''}{delta} vs last week
        </p>
      )}
    </div>
  );
}

function SilentTab({ accounts }: { accounts: SilentAccount[] }) {
  if (accounts.length === 0) {
    return <p className="text-center py-12 text-sm text-gray-500">All accounts have recent activity.</p>;
  }

  return (
    <div className="space-y-2">
      <p className="text-sm text-gray-500">{accounts.length} accounts with no leads in the last 30 days</p>
      {accounts.map(a => (
        <div key={a.id} className="bg-white rounded-xl border border-gray-100 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900 text-sm">{a.name}</p>
              <p className="text-xs text-gray-500 mt-0.5">
                {a.admin_email ?? 'No admin'} | {a.status}
                {a.last_lead_at ? ` | Last lead: ${new Date(a.last_lead_at).toLocaleDateString()}` : ' | Never received a lead'}
              </p>
            </div>
            <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${
              a.subscription_status === 'trial' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-600'
            }`}>{a.subscription_status}</span>
          </div>
        </div>
      ))}
    </div>
  );
}
